import { FC, useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Button, Tooltip } from '@smartshell/ui-kit';

import { AuthContext, EndWorkShiftModal } from 'auth';
import { LINKS } from 'links';
import { LocalizationContext } from 'localization';
import { LogoutAllModal } from 'modals';
import { NewsContext } from 'news';
import { isSandbox } from 'sandbox';
import {
  getOnboardingControls,
  restartSandboxOnboarding,
} from 'sandbox/onboarding';

import { LanguageSwitcher } from '../LanguageSwitcher';
import s from './Controls.module.scss';
import { GET_WORKSHIFT_DATA, IGetWorkShiftDataResponse } from './schema';

export const Controls: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const onboardingControls = useMemo(() => getOnboardingControls(lang), [lang]);

  const {
    actions: { logout },
    data: { permissions, isActiveOperator },
  } = useContext(AuthContext);

  const { showNews } = useContext(NewsContext);

  const [isEndWorkShiftMWOpen, setIsEndWorkShiftMWOpen] = useState(false);
  const [isLogoutAllModalOpen, setIsLogoutAllModalOpen] = useState(false);

  const { data: workshiftDataResponse } = useQuery<IGetWorkShiftDataResponse>(
    GET_WORKSHIFT_DATA,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
      skip: !permissions?.has('work_shift_active'),
    }
  );

  const isCloseWorkShift = useMemo(() => {
    if (!workshiftDataResponse?.getWorkShiftPaymentOverviewData) return false;

    return !(!isActiveOperator && !permissions?.has('worker_manage'));
  }, [permissions, workshiftDataResponse, isActiveOperator]);

  return (
    <>
      {isEndWorkShiftMWOpen && (
        <EndWorkShiftModal onClose={() => setIsEndWorkShiftMWOpen(false)} />
      )}
      {isLogoutAllModalOpen && (
        <LogoutAllModal onClose={() => setIsLogoutAllModalOpen(false)} />
      )}
      {isCloseWorkShift && (
        <Button
          onClick={() => setIsEndWorkShiftMWOpen(true)}
          variant="ghost"
          className={s.control}
          iconName="FinishIcon"
        >
          {t('workShifts.active.finish')}
        </Button>
      )}
      <Button
        onClick={() => open(LINKS.home[lang])}
        variant="ghost"
        className={s.control}
        iconName="InfoIcon"
      >
        {t('helpCenter')}
      </Button>
      <Button
        onClick={showNews}
        variant="ghost"
        className={s.control}
        iconName="SpeakerIcon"
      >
        {t('news')}
      </Button>
      <Button
        variant="ghost"
        className={s.control}
        iconName="IdeaIcon"
        onClick={() => window?.involveo?.openPopup('backlog')}
      >
        {t('idea.action')}
      </Button>
      <LanguageSwitcher />
      <div className={s.logout}>
        <Button
          onClick={logout}
          variant="ghost"
          className={s.control}
          iconName="SignOutIcon"
        >
          {t('logout')}
        </Button>
        <Tooltip
          content={t('logout.all.tooltip')}
          className={s.logout_tooltip}
          contentClassName={s.logout_tooltip__content}
          position="bottom-right"
        >
          <Button
            onlyIcon
            variant="ghost"
            iconName="SignOutAllIcon"
            className={s.logout__all}
            onClick={() => setIsLogoutAllModalOpen(true)}
          />
        </Tooltip>
      </div>
      {isSandbox && (
        <>
          <div className={s.onboarding_separator} />
          <Button
            onClick={restartSandboxOnboarding}
            variant="ghost"
            className={s.control}
            iconName="InfoIcon"
          >
            {onboardingControls.restart}
          </Button>
        </>
      )}
    </>
  );
};
